import React, { useEffect, useState, useRef, useCallback, memo } from "react";
import MiniTitle from "layouts/MiniTitle";
import MainTitle from "layouts/MainTitle";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { motion, useInView, AnimatePresence } from "framer-motion";
import "swiper/css";
import "styles/public.css";
import teacherFallback from "assets/layout_img/teacher.jpg";
import { FiArrowLeftCircle, FiArrowRightCircle } from "react-icons/fi";

const API_URL =
  "https://algoritm-api-edu.uz/api/public/MainPageUi/GetAllTeachers";
const BASE_IMG_URL = "https://algoritm-api-edu.uz/";

const TeacherCard = memo(({ teacher, isActive, onClick }) => {
  return (
    <motion.div
      className={`teacher-card ${isActive ? "active" : "inactive"}`}
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
      animate={{
        scale: isActive ? 1.12 : 0.92,
        opacity: isActive ? 1 : 0.75,
        filter: isActive ? "brightness(1)" : "brightness(0.8)",
      }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      layout
    >
      <div className="teacher-image-wrapper">
        {isActive && (
          <motion.div
            className="active-glow"
            layoutId={`glow-${teacher.id}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1.2 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          />
        )}
        <img
          src={teacher.imageUrl || teacherFallback}
          alt={teacher.fullName}
          className="teacher-img"
          loading="lazy"
          onError={(e) => {
            e.target.src = teacherFallback;
            e.target.alt = "O'qituvchi";
          }}
        />
      </div>
    </motion.div>
  );
});
TeacherCard.displayName = "TeacherCard";

const TeacherInfoCard = memo(({ teacher, activeIndex }) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeIndex}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -30 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className="w-full text-center"
        role="article"
        aria-labelledby={`teacher-name-${activeIndex}`}
      >
        <h3
          id={`teacher-name-${activeIndex}`}
          className="text-3xl font-bold mb-2"
        >
          {teacher?.fullName || "Ism mavjud emas"}
        </h3>
        <p className="teacher-position text-lg text-orange-600 mb-4">
          {teacher?.position || "Lavozim ko'rsatilmagan"}
        </p>
        <p className="teacher-bio text-gray-700 leading-relaxed">
          {teacher?.bio || "Bu o'qituvchi haqida ma'lumot kiritilmagan."}
        </p>
      </motion.div>
    </AnimatePresence>
  );
});
TeacherInfoCard.displayName = "TeacherInfoCard";

const NavigationButton = memo(({ onClick, direction, disabled }) => {
  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`info-nav-btn info-nav-btn-${direction}`}
      animate={disabled ? { opacity: 0.5 } : { opacity: 1 }}
      transition={{ duration: 0.3 }}
      aria-label={
        direction === "left" ? "Oldingi o'qituvchi" : "Keyingi o'qituvchi"
      }
      aria-disabled={disabled}
    >
      {direction === "left" ? (
        <FiArrowLeftCircle size={32} />
      ) : (
        <FiArrowRightCircle size={32} />
      )}
    </motion.button>
  );
});
NavigationButton.displayName = "NavigationButton";

const Teacher = memo(() => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const swiperRef = useRef(null);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const fetchTeachers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          search: "",
          sortBy: "",
          orderType: "",
          pageSize: 50,
          page: 1,
          startDate: null,
          endDate: null,
        }),
      });

      if (!response.ok) throw new Error("Server xatosi");

      const data = await response.json();
      const rows = data?.rows || [];
      console.log("O'qituvchilar ma'lumotlari:", rows);

      const formattedTeachers = rows.map((t, i) => ({
        id: t.id || `teacher-${i + 1}`,
        fullName: t.fullName?.trim() || "Ism mavjud emas",
        position: t.position?.trim() || "Lavozim ko'rsatilmagan",
        bio: (t.bio || "Bu o'qituvchi haqida ma'lumot kiritilmagan.").trim(),
        imageUrl: t.imgUrl ? `${BASE_IMG_URL}${t.imgUrl}` : null,
      }));

      setTeachers(formattedTeachers);
      setActiveIndex(0);
    } catch (err) {
      console.error("O'qituvchilar yuklanmadi:", err);
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const handleSlideChange = useCallback((swiper) => {
    setActiveIndex(swiper.realIndex);
  }, []);

  const goNext = useCallback(() => {
    swiperRef.current?.swiper?.slideNext();
  }, []);

  const goPrev = useCallback(() => {
    swiperRef.current?.swiper?.slidePrev();
  }, []);

  const handleCardClick = useCallback((index) => {
    swiperRef.current?.swiper?.slideToLoop(index);
  }, []);

  if (loading) {
    return (
      <section className="teacher-section" ref={containerRef}>
        <div className="teacher-container">
          <MiniTitle>O'qituvchilar</MiniTitle>
          <MainTitle>Yuklanmoqda...</MainTitle>
          <div className="skeleton-carousel">
            <div className="skeleton-card"></div>
            <div className="skeleton-card"></div>
            <div className="skeleton-card"></div>
          </div>
        </div>
      </section>
    );
  }

  if (!teachers.length) {
    return (
      <section className="teacher-section" ref={containerRef}>
        <div className="teacher-container">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <MiniTitle>O'qituvchilar</MiniTitle>
            <MainTitle>Hozircha o'qituvchilar yo'q</MainTitle>
          </motion.div>
          <div className="empty-state">
            <p>O'qituvchilar qo'shilmoqda...</p>
          </div>
        </div>
      </section>
    );
  }

  const currentTeacher = teachers[activeIndex];

  return (
    <section className="teacher-section" id="teachers" ref={containerRef}>
      <div className="teacher-container">
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <MiniTitle>O'qituvchilar</MiniTitle>
          <MainTitle>O'qituvchilar galeriyasi</MainTitle>
        </motion.div>

        <div className="teacher-carousel-wrapper">
          <div className="swiper-centered-container">
            <Swiper
              ref={swiperRef}
              modules={[Autoplay]}
              loop={true}
              centeredSlides={true}
              slidesPerView={3}
              spaceBetween={40}
              speed={800}
              grabCursor={true}
              autoplay={false}
              onSlideChange={handleSlideChange}
              breakpoints={{
                320: { slidesPerView: 1.3, spaceBetween: 20 },
                640: { slidesPerView: 2.2, spaceBetween: 30 },
                768: { slidesPerView: 2.5, spaceBetween: 40 },
                1024: { slidesPerView: 3, spaceBetween: 50 },
                1280: { slidesPerView: 3, spaceBetween: 60 },
              }}
              className="teacher-swiper"
            >
              {teachers.map((teacher, index) => (
                <SwiperSlide key={teacher.id}>
                  <TeacherCard
                    teacher={teacher}
                    isActive={index === activeIndex}
                    onClick={() => handleCardClick(index)}
                    index={index}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>

        <div className="teacher-info-wrapper">
          <NavigationButton
            onClick={goPrev}
            direction="left"
            disabled={loading}
          />

          <NavigationButton
            onClick={goNext}
            direction="right"
            disabled={loading}
          />

          <motion.div
            className="teacher-info-card"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="deco-circle deco-left" aria-hidden="true"></div>
            <div className="deco-circle deco-right" aria-hidden="true"></div>

            <TeacherInfoCard
              teacher={currentTeacher}
              activeIndex={activeIndex}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
});

Teacher.displayName = "TeacherSection";
export default Teacher;
